<template>
    <div class="gallery-slides">
        <!-- Swiper container -->
        <div class="gallery-swiper-container">
            <div class="swiper-wrapper">
                <div class="swiper-slide" v-for="(item, index) in images" :key="index">
                    <div class="swiper-slide2">
                        <div class="image-container" :id="`image-container-${index}`">
                            <div :id="`zoom-content-${index}`" class="zoom-viewer">
                                <!-- If seadragon is disabled, just show the image -->
                                <img
                                    v-if="!seadragonEnabled"
                                    :src="`/assets/${item.img}`"
                                    :alt="item.caption"
                                    style="width: 100%; height: 100%; object-fit: contain;"
                                />
                            </div>
                            <div class="caption">{{ item.caption }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="swiper-pagination"></div>
            <div class="swiper-button-next"></div>
            <div class="swiper-button-prev"></div>
        </div>
    </div>
</template>

<script>
import Swiper from 'swiper';
import { Autoplay, EffectFade, Navigation, Pagination, Keyboard } from 'swiper/modules';
import OpenSeadragon from 'openseadragon';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export default {
    name: "GalleryBlock",
    props: {
        images: {
            type: Array,
            required: true,
        },
        seadragonEnabled: {
            type: Boolean,
            required: false,
            default: false
        },
    },
    data() {
        return {
            viewers: [],
            swiperInstance: null,
        };
    },
    mounted() {
        this.$nextTick(() => {
            // Initialize Swiper with conditional allowTouchMove
            this.swiperInstance = new Swiper('.gallery-swiper-container', {
                modules: [Autoplay, EffectFade, Navigation, Pagination, Keyboard],
                init: false,
                speed: 750,
                loop: true,
                // Enable touch move if seadragon is disabled
                allowTouchMove: !this.seadragonEnabled,
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                },
                pagination: {
                    el: '.swiper-pagination',
                    clickable: true,
                },
                keyboard: {
                    enabled: true,
                    onlyInViewport: false,
                },
                on: {
                    slideChange: () => {
                        this.$nextTick(() => {
                            this.updateSizes(this.swiperInstance.activeIndex);
                        });
                    },
                },
            });

            setTimeout(() => this.swiperInstance.init(), 100);

            // Delay initial sizing and initialization
            setTimeout(() => {
                this.updateSizes(this.swiperInstance.activeIndex);
            }, 500);

            window.addEventListener('resize', this.handleResize);
        });
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() {
            this.updateSizes(this.swiperInstance.activeIndex);
        },
        updateSizes(index) {
            const activeSlide = this.$el.querySelector(`.swiper-slide:nth-child(${index + 1}) .swiper-slide2`);
            const imageContainer = this.$el.querySelector(`#image-container-${index}`);
            const viewerContainer = this.$el.querySelector(`#zoom-content-${index}`);

            if (!activeSlide || !imageContainer || !viewerContainer) return;

            let newWidth = activeSlide.clientWidth;
            let newHeight = newWidth / this.images[index].aspectRatio;

            if (newHeight > activeSlide.clientHeight) {
                newHeight = activeSlide.clientHeight;
                newWidth = newHeight * this.images[index].aspectRatio;
            }

            imageContainer.style.width = `${newWidth}px`;
            imageContainer.style.height = `${newHeight}px`;
            viewerContainer.style.width = `${newWidth}px`;
            viewerContainer.style.height = `${newHeight}px`;

            // Only initialize OpenSeadragon if enabled
            if (this.seadragonEnabled) {
                if (!this.viewers[index]) {
                    this.viewers[index] = OpenSeadragon({
                        id: `zoom-content-${index}`,
                        maxZoomLevel: 10,
                        prefixUrl: '/assets/seadragon/',
                        showNavigator: true,
                        navigatorPosition: "TOP_RIGHT",
                        tileSources: {
                            type: 'image',
                            url: `/assets/${this.images[index].img}`,
                        },
                    });
                } else {
                    this.viewers[index].resize();
                    this.viewers[index].forceRedraw();
                }
            }
        },
    },
};
</script>

<style scoped lang="scss">
.zoom-viewer {
    width: 100%;
    height: 100%;
    position: relative;
}
</style>
